/**
 * Shared API Client for all signup forms
 * Handles geolocation fetching and lead submission
 */

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch visitor's geolocation data
 * Returns: { isSuccess, data: { ipAddress, countryCode, countryName, regionName, cityName, latitude, longitude }, errorMessage, statusCode }
 */
export async function getGeoLocation() {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain',
      },
    });

    if (!response.ok) {
      throw new Error(`Geolocation API error: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Geolocation fetch error:', error);
    return {
      isSuccess: false,
      data: null,
      errorMessage: error.message,
      statusCode: 500,
    };
  }
}

/**
 * Submit lead to Lead API
 * @param {Object} payload - Lead data with required fields
 * @returns {Object} API response with statusCode, data, message, or errorMessage
 */
export async function submitLead(payload) {
  try {
    const response = await fetch(LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    
    // Add statusCode if not present
    if (!data.statusCode) {
      data.statusCode = response.status;
    }

    return data;
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      isSuccess: false,
      statusCode: 500,
      errorMessage: 'Network error. Please try again.',
    };
  }
}

/**
 * Build lead payload from form data and geolocation
 * @param {Object} formData - Form input values
 * @param {Object} geoData - Geolocation API response
 * @returns {Object} Formatted payload for Lead API
 */
export function buildLeadPayload(formData, geoData) {
  // Clean phone number - remove all non-numeric characters
  const cleanPhone = (formData.phone || formData.phoneNumber || '').replace(/\D/g, '');
  
  // Get current page URL
  const pageUrl = window.location.href;

  // Build required fields
  const payload = {
    name: formData.name,
    email: formData.email,
    countryDialCode: Number(formData.countryCode?.replace('+', '') || formData.countryDialCode || 1),
    country: geoData?.data?.countryCode || 'US',
    phoneNumber: cleanPhone,
    ipAddress: geoData?.data?.ipAddress || '',
    description: formData.message || formData.description || 'Contact form submission',
    pageUrl: pageUrl,
  };

  // Add optional fields from geolocation if available
  if (geoData?.data?.cityName) {
    payload.city = geoData.data.cityName;
  }
  
  if (geoData?.data?.regionName) {
    payload.state = geoData.data.regionName;
  }

  // Add full geolocation data as stringified JSON
  if (geoData) {
    payload.geoLocation = JSON.stringify(geoData);
  }

  // Add package information if available
  if (formData.packageTitle) {
    payload.packageTitle = formData.packageTitle;
  }
  
  if (formData.packageAmount) {
    payload.packageAmount = Number(formData.packageAmount) || 0;
  }

  return payload;
}

/**
 * Handle Lead API response
 * @param {Object} response - API response
 * @param {Function} onError - Error callback
 */
export function handleLeadResponse(response, onError) {
  if (response.statusCode === 200 && response.data?.customerId) {
    // Success - redirect to application page
    window.location.href = `/application/main?customerid=${response.data.customerId}`;
    return true;
  } else if (response.statusCode === 401 && response.message) {
    // Unauthorized - show message
    onError(response.message);
    return false;
  } else if (response.statusCode === 400 && response.errorMessage) {
    // Bad Request - show errorMessage
    onError(response.errorMessage);
    return false;
  } else {
    // Generic error
    onError(response.errorMessage || response.message || 'Something went wrong. Please try again.');
    return false;
  }
}
